import { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { renderers } from '@features/analysis/rendering/Panels/renderState';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';

interface HistogramChartProps {
  mainIndex: number;
  data: number[];
  width: number;
  height: number;
  yLabel?: string;
  xLabel?: string;
  lineWidth?: number; // 선 두께
  mean?: number; // mean 값
  sd?: number; // sd 값
  smooth?: boolean; // true: 스무딩 적용, false: 원본
  smoothWindow?: number; // 스무딩 window, 기본 5
}

const HU_MIN = -190;
const HU_MAX = -30;

const colorTable = [
  { start: -30, end: -54, color: 'rgb(255,0,0)' },
  { start: -55, end: -71, color: 'rgb(255,102,0)' },
  { start: -72, end: -122, color: 'rgb(255,255,0)' },
  { start: -123, end: -190, color: 'rgb(245,255,149)' },
];

function movingAverage(arr: number[], windowSize: number): number[] {
  if (windowSize <= 1) return arr.slice();
  const result = [];
  for (let i = 0; i < arr.length; ++i) {
    let start = Math.max(0, i - Math.floor(windowSize / 2));
    let end = Math.min(arr.length, i + Math.ceil(windowSize / 2));
    let sum = 0,
      count = 0;
    for (let j = start; j < end; ++j) {
      sum += arr[j];
      count++;
    }
    result.push(sum / count);
  }
  return result;
}
function saveSvgAsPng(svgElement: SVGSVGElement, filename: string = 'capture.png') {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  const svgDataUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
    return svgDataUri;
  }
export default function HistogramChart({
  mainIndex,
  data,
  width,
  height,
  yLabel = 'Volume',
  xLabel = 'HU',
  lineWidth = 2.5,
  mean,
  sd,
  smooth = true,
  smoothWindow = 5,
}: HistogramChartProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const chartScale = Math.max(
    0.62,
    Math.min(1.22, Math.min(width / 300, height / 220) * getDisplayRuntimeProfile().typographyScale),
  );
  const padding = {
    top: Math.round(40 * chartScale),
    right: Math.round(20 * chartScale),
    bottom: Math.round(50 * chartScale),
    left: Math.round(80 * chartScale),
  };
  const axisFontSize = `${Math.max(10, Math.round(16 * chartScale))}px`;
  const labelFontSize = `${Math.max(12, Math.round(19 * chartScale))}px`;
  const meanFontSize = `${Math.max(11, Math.round(16 * chartScale))}px`;

  const rendererId = useMemo(()=>`Histogram.${mainIndex}`,[mainIndex]);
  renderers[rendererId]={
    render:(args?:any)=>{
      const {type, key,setter} = args;
      switch(type){
        case 'captureRequest':
          const svgURL = saveSvgAsPng(svgRef.current!);
          setter(key,{histogram:svgURL});
        break;
      }
    }
  }
  const usedData = useMemo(() => (smooth ? movingAverage(data, smoothWindow) : data), [data, smooth, smoothWindow]);
  const huList = useMemo(() => d3.range(HU_MIN, HU_MAX + 1, 1).slice(0, data.length), [data.length]);
  const yMax = useMemo(() => d3.max(usedData) ?? 1, [usedData]);

  const xScale = useMemo(
    () => d3.scaleLinear().domain([HU_MIN, HU_MAX]).range([padding.left, width - padding.right]),
    [width]
  );
  const yScale = useMemo(
    () => d3.scaleLinear().domain([0, yMax]).range([height - padding.bottom, padding.top]),
    [height, yMax]
  );

  const lineData = useMemo(() => usedData.map((v, i) => [huList[i], v] as [number, number]), [usedData, huList]);

  const clearChart = (target: 'all' | 'dataOnly') => {
    const svg = d3.select(svgRef.current);

    if (target === 'all') {
      svg.selectAll('*').remove();
      svg
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background', 'transparent');
    } else {
      svg.selectAll('.colored-area').remove();
      svg.selectAll('.mean-line').remove();
      svg.selectAll('.mean-text').remove();
      svg.selectAll('.y-axis').remove();
    }
  };

  const drawXAxes = () => {
    const svg = d3.select(svgRef.current);
    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height - padding.bottom})`)
      .call(d3.axisBottom(xScale).tickValues(d3.range(HU_MIN, HU_MAX + 1, 40)).tickFormat((d) => `${d}`))
      .attr('color', '#fff')
      .attr('font-size', axisFontSize);
  }

  const drawYAxes = () => {
    const svg = d3.select(svgRef.current);

    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${padding.left},0)`)
      .call(
        d3.axisLeft(yScale)
          .ticks(5)
      )
      .attr('color', '#fff')
      .attr('font-size', axisFontSize);
  }

  const drawColoredAreas = () => {
    const svg = d3.select(svgRef.current);

    colorTable.forEach(({ start, end, color }, idx) => {
      const i0 = Math.max(0, start - HU_MIN);
      const i1 = Math.min(data.length - 1, end - HU_MIN);
      const sliceStart = Math.min(i0, i1, 0);
      let sliceEnd = Math.max(i0, i1);
      if (idx < colorTable.length - 1) sliceEnd += 1;

      if (sliceStart < sliceEnd) {
        const segData = lineData.slice(sliceStart, sliceEnd + 1);

        svg
          .append('path')
          .attr('class', 'colored-area')
          .datum(segData)
          .attr('fill', color)
          .attr('fill-opacity', 0.6)
          .attr('stroke', 'none')
          .attr(
            'd',
            d3
              .area<[number, number]>()
              .x((d) => xScale(d[0]))
              .y0(yScale(0))
              .y1((d) => yScale(d[1]))
              .curve(d3.curveMonotoneX) as any,
          );

        svg
          .append('path')
          .attr('class', 'colored-area')
          .datum(segData)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', lineWidth)
          .attr(
            'd',
            d3
              .line<[number, number]>()
              .x((d) => xScale(d[0]))
              .y((d) => yScale(d[1]))
              .curve(d3.curveMonotoneX) as any,
          );
      }
    });
  };

  const drawMeanSd = () => {
    if (typeof mean !== 'number' || mean < HU_MIN || mean > HU_MAX) return;
    const svg = d3.select(svgRef.current);
    const x = xScale(mean);
    const meanY = Math.round(50 * chartScale);
    const sdY = meanY + Math.round(18 * chartScale);
    const textX = x - Math.round(90 * chartScale);
    const anchor = 'start';
    svg
      .append('line')
      .attr('class', 'mean-line')
      .attr('x1', x)
      .attr('x2', x)
      .attr('y1', yScale(0))
      .attr('y2', yScale(yMax))
      .attr('stroke', '#29f')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4 2');

    svg
      .append('text')
      .attr('class', 'mean-text')
      .attr('x', textX)
      .attr('y', meanY)
      .attr('fill', '#29f')
      .attr('font-size', meanFontSize)
      .attr('font-weight', 'bold')
      .attr('text-anchor', anchor)
      .text(`Mean: ${mean.toFixed(1)}`);

    if (typeof sd === 'number') {
      svg
        .append('text')
        .attr('class', 'mean-text')
        .attr('x', textX)
        .attr('y', sdY)
        .attr('fill', '#29f')
        .attr('font-size', meanFontSize)
        .attr('font-weight', 'bold')
        .attr('text-anchor', anchor)
        .text(`SD: ${sd.toFixed(1)}`);
    }
  };

  const drawXLabels = () => {
    const svg = d3.select(svgRef.current);

    svg
      .append('text')
      .attr('class', 'x-label')
      .attr('x', (width / 2) + 30)
      .attr('y', height - 8)
      .attr('fill', 'white')
      .attr('text-anchor', 'middle')
      .attr('font-size', labelFontSize)
      .text(xLabel);
  };

  const drawYLabels = () => {
    const svg = d3.select(svgRef.current);

    svg
      .append('text')
      .attr('class', 'y-label')
      .attr('transform', `rotate(-90)`)
      .attr('x', -height / 2)
      .attr('y', 24)
      .attr('fill', '#FFD700')
      .attr('text-anchor', 'middle')
      .attr('font-size', labelFontSize)
      .text(`${yLabel} (mm³)`);
  };

  useEffect(() => {
    if(!svgRef.current) return;
    clearChart('all');
    drawXAxes();
    drawXLabels();
    drawYLabels();
    drawYAxes();
    drawColoredAreas();
    drawMeanSd();
  }, [width, height, chartScale]);

  useEffect(() => {
    if(!svgRef.current) return;
    clearChart('dataOnly');
    drawYAxes();
    drawColoredAreas();
    drawMeanSd();
  }, [data]);

  return (
    <svg
      ref={svgRef}
      style={{
        userSelect: 'none',
        width: '100%',
        height: 'auto',
        display: 'block',
      }}
    />
  );
}
